import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Presentation } from "lucide-react";
import { listPublishedPresentations } from "@/lib/api/presentations.functions";

export const Route = createFileRoute("/presentations/")({
  head: () => ({
    meta: [
      { title: "Class Presentations | Nkadimeng Danny Legal" },
      {
        name: "description",
        content:
          "Law class presentations from South African students — Higher Certificate, Diploma and LLB slide decks on real legal topics.",
      },
      { property: "og:title", content: "Class Presentations — SA law students" },
      {
        property: "og:description",
        content: "Student legal presentations you can view, download and share.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/presentations" }],
  }),
  component: PresentationsIndex,
});

function PresentationsIndex() {
  const list = useServerFn(listPublishedPresentations);
  const q = useQuery({ queryKey: ["presentations"], queryFn: () => list() });

  return (
    <div className="container-narrow py-12">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Student showcase</p>
        <h1 className="mt-1 font-display text-3xl text-navy">Class presentations</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Slide decks prepared by law students across the Higher Certificate, Diploma and LLB
          programmes. View, download and share them.
        </p>
      </header>

      {q.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-navy" />
      ) : (q.data?.presentations.length ?? 0) === 0 ? (
        <p className="text-sm text-muted-foreground">No presentations published yet.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {q.data!.presentations.map((p: any) => (
            <Link
              key={p.id}
              to="/presentations/$slug"
              params={{ slug: p.slug }}
              className="group rounded-lg border border-border bg-card p-4 transition-colors hover:border-navy"
            >
              {p.cover_image_url ? (
                <img
                  src={p.cover_image_url}
                  alt={p.title}
                  width={640}
                  height={360}
                  loading="lazy"
                  className="mb-3 aspect-video w-full rounded-md object-cover"
                />
              ) : (
                <div className="mb-3 flex aspect-video w-full items-center justify-center rounded-md bg-navy/5">
                  <Presentation className="h-8 w-8 text-navy/40" />
                </div>
              )}
              {p.course_label && (
                <p className="text-xs uppercase tracking-widest text-gold">{p.course_label}</p>
              )}
              <h2 className="mt-1 font-display text-lg text-navy group-hover:underline">{p.title}</h2>
              {p.summary && (
                <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{p.summary}</p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
